import { convertToMomentJs, eventTypeConvert } from '@/shared/lib/aiUtils';

export const EVENT_OTHER_TYPE = 'OTHERS';

/**
 * Builds the error text shown when a new ECG event overlaps one that already exists. Maps the event type through eventTypeConvert, falling back to the raw type, and formats the event start and stop with convertToMomentJs as HH:mm:ss in report local time, embedding both in a bold bracketed label.
 *
 * @param event - The clashing event, read for its type, start and stop.
 * @param timezoneOffset - Offset used to render the times in report local time.
 * @returns The HTML error message naming the conflicting event.
 */
export const generateAddEventExistErrorMessage = (event: any, timezoneOffset: number) =>
  `An event <b>[${eventTypeConvert[event.type as keyof typeof eventTypeConvert] || event.type} - ${convertToMomentJs(event.start, timezoneOffset)?.format('HH:mm:ss')} -> ${convertToMomentJs(event.stop, timezoneOffset)?.format('HH:mm:ss')}]</b> already exists, so a new event cannot be created.`;

export const UPDATE_EVENT_NO_DATA_ERROR_MESSAGE =
  'It is not possible to create/update an event that lacks a data area.';

export const ADD_EVENT_NO_BEAT_ERROR_MESSAGE =
  'It is not possible to create/update an event less than 2 beats.';

export const ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE =
  'It is not possible to create/update an AFib event less than 5 seconds.';

export const UPDATE_EVENT_WITH_NEW_RANGE_ERROR_MESSAGE =
  "Updating the duration is not possible. Please utilize the 'Add new event' function for this purpose.";

export const ADD_AFIB_TO_ARTIFACT_ERROR_MESSAGE =
  'It is not possible to add/update an AFib event to an artifact event';
export const ADD_ARTIFACT_TO_AFIB_ERROR_MESSAGE =
  'It is not possible to add/update an artifact event to an AFib event';
export const CHANGE_EVENT_SAME_DURATION_ERROR_MESSAGE =
  'It is not possible to update an event with the same duration.';

/**
 * Builds the error text shown when an edit would place an event inside another event. Resolves the enclosing event's type label through eventTypeConvert, falling back to the raw type, and renders its start and stop with convertToMomentJs as HH:mm:ss in report local time.
 *
 * @param event - The enclosing event, read for its type, start and stop.
 * @param timezoneOffset - Offset used to render the times in report local time.
 * @returns The HTML error message naming the enclosing event.
 */
export const generateUpdateEventInOtherEventErrorMessage = (event: any, timezoneOffset: number) =>
  `It is not possible to update an event within another event <b>[${eventTypeConvert[event.type as keyof typeof eventTypeConvert] || event.type} - ${convertToMomentJs(event.start, timezoneOffset)?.format('HH:mm:ss')} -> ${convertToMomentJs(event.stop, timezoneOffset)?.format('HH:mm:ss')}]</b>.`;

/**
 * Builds the error text shown when changing an event's type would collide with an existing event. Resolves the type label through eventTypeConvert, falling back to the raw type, and formats the existing event's start and stop with convertToMomentJs as HH:mm:ss in report local time.
 *
 * @param event - The blocking event, read for its type, start and stop.
 * @param timezoneOffset - Offset used to render the times in report local time.
 * @returns The HTML error message explaining that the type change is blocked.
 */
export const generateChangeEventExistErrorMessage = (event: any, timezoneOffset: number) =>
  `An event <b>[${eventTypeConvert[event.type as keyof typeof eventTypeConvert] || event.type} - ${convertToMomentJs(event.start, timezoneOffset)?.format('HH:mm:ss')} -> ${convertToMomentJs(event.stop, timezoneOffset)?.format('HH:mm:ss')}]</b> exists, so the selected event cannot be changed to the other types.`;

export const ADD_EVENT_UNDER_3_BEATS_ERROR_MESSAGE =
  'It is not possible to create/update an event less than 3 beats.';
export const CHANGE_EVENT_TYPE_RUN_UNDER_3_BEATS_ERROR_MESSAGE =
  'This event requires at least 3 beats to switch to SVE/VE Run.';
